import React from "react";
import Slider from "@mui/material/Slider";

const COMPONENT_CLASS = "c-slider-input-option";

export interface SliderInputOptionProps {
    onChange: (newAnswer: string) => void;
    answer?: string;
    disableInput?: boolean;
    enableTopRangeLabels?: boolean;
    topRangeLabelLeft?: string;
    topRangeLabelRight?: string;
    bottomRangeLabelLeft?: string;
    bottomRangeLabelRight?: string;
    gradient?: boolean;
    min?: number; // logical min (e.g. 0)
    max?: number; // logical max (e.g. 10)
    smoothFactor?: number; // e.g. 10 → step = 0.1
}

const SliderInputOption: React.FC<SliderInputOptionProps> = (props) => {
    const logicalMin = props.min ?? 0;
    const logicalMax = props.max ?? 100;

    const smooth = props.smoothFactor ?? 1;
    // smoothFactor = 1 → step = 1 (original behavior)
    // smoothFactor = 10 → step = 0.1 (much smoother)
    const step = 1 / smooth;

    const currentValueRaw =
        props.answer !== undefined ? parseFloat(props.answer) : logicalMin;
    const currentValue = Number.isNaN(currentValueRaw)
        ? logicalMin
        : currentValueRaw;

    const handleChange = (val: number | number[]) => {
        if (typeof val === "number") {
            // Pass the raw slider value (0–10 with decimals) up as string
            props.onChange(val.toString());
        }
    };

    return (
        <div className={`${COMPONENT_CLASS}`}>
            {/* Integer input field */}
            {!props.disableInput && (
                <div className={`${COMPONENT_CLASS}__integer-wrapper`}>
                    <div className={`${COMPONENT_CLASS}__integer-options`}>
                        <input
                            className={`${COMPONENT_CLASS}__input`}
                            type="number"
                            min={logicalMin}
                            max={logicalMax}
                            value={props.answer ?? currentValue}
                            onChange={(e) => {
                                let val = parseFloat(e.target.value);
                                if (val > logicalMax) val = logicalMax;
                                if (val < logicalMin) val = logicalMin;
                                props.onChange(val.toString());
                            }}
                        />
                    </div>
                    <span>%</span>
                </div>
            )}

            {/* Slider wrapper */}
            <div className={`${COMPONENT_CLASS}__slider-wrapper`}>
                {/* Top range labels */}
                {props.enableTopRangeLabels &&
                    props.topRangeLabelLeft &&
                    props.topRangeLabelRight && (
                        <div className={`${COMPONENT_CLASS}__top-range-wrapper`}>
                            <label>{props.topRangeLabelLeft}</label>
                            <label>{props.topRangeLabelRight}</label>
                        </div>
                    )}

                {/* Slider */}
                <Slider
                    min={logicalMin}
                    max={logicalMax}
                    step={step}
                    value={currentValue}
                    onChange={(e, val) => handleChange(val)}
                    sx={{
                        height: "10px",
                        borderRadius: "5px",
                        ...(props.gradient && {
                            "& .MuiSlider-rail": {
                                background:
                                    "linear-gradient(90deg, #4caf50 0%, #ffeb3b 50%, #f44336 100%)",
                                opacity: 1,
                            },
                            "& .MuiSlider-track": {
                                backgroundColor: "transparent", // transparent track so gradient shows through
                                border: "none",
                            },
                        })
                    }}
                />

                {/* Bottom range labels */}
                <div className={`${COMPONENT_CLASS}__bottom-range-wrapper`}>
                    <label>
                        {props.bottomRangeLabelLeft !== undefined
                            ? props.bottomRangeLabelLeft
                            : logicalMin}
                    </label>
                    <label>
                        {props.bottomRangeLabelRight !== undefined
                            ? props.bottomRangeLabelRight
                            : logicalMax}
                    </label>
                </div>
            </div>
        </div>
    );
};

export default SliderInputOption;
